import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import customerActivityService from '../../../api/crm/customers/customerActivityService';
import { useAuth } from '../../../context/AuthContext';
import { USER_ROLES } from '../../../utils/constants';
import { toast } from 'react-hot-toast';
import DOMPurify from 'dompurify';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Phone,
  Mail,
  MessageSquare,
  Calendar,
  FileText,
  Clock,
  User,
  Building,
  X,
  CheckCircle,
  XCircle,
  Download
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import { formatDate as formatSystemDate } from '../../../utils/stringUtils';

const CustomerActivityDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSalesExecutive = user?.role === USER_ROLES.SALES_EXECUTIVE || user?.role === USER_ROLES.EXPORT_EXECUTIVE || user?.role === USER_ROLES.BUSINESS_RELATIONSHIP_MANAGER || user?.role === USER_ROLES.BUSINESS_DEVELOPMENT_EXECUTIVE;
  const [searchParams] = useSearchParams();
  const isEditMode = searchParams.get('edit') === 'true';

  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    const fetchActivity = async () => {
      if (!id) {
        setError('Activity ID is required');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const result = await customerActivityService.getActivityById(id);
        if (result.success) {
          setActivity(result.activity);
        } else {
          setError(result.error || 'Failed to load activity');
          toast.error(result.error || 'Failed to load activity');
        }
      } catch (error) {
        setError('An error occurred while loading activity');
        toast.error('An error occurred while loading activity');
      } finally {
        setLoading(false);
      }
    };

    fetchActivity();
  }, [id]);

  const getActivityIcon = (type) => {
    switch (type) {
      case 'call': return Phone;
      case 'email': return Mail;
      case 'whatsapp': return MessageSquare;
      case 'meeting': return Calendar;
      case 'note': return FileText;
      default: return FileText;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'call': return 'text-blue-600';
      case 'email': return 'text-green-600';
      case 'whatsapp': return 'text-green-500';
      case 'meeting': return 'text-purple-600';
      case 'note': return 'text-gray-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'sent': return 'bg-green-100 text-green-800';
      case 'scheduled': return 'bg-blue-100 text-blue-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800';
      case 'rescheduled': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCustomerName = (customer) => {
    if (!customer) return 'Unknown Customer';
    if (typeof customer === 'string') return customer;
    return customer.companyName || customer.contactPerson || customer.customerId || customer.email || 'Unknown Customer';
  };

  const getAssigneeDetails = (performedBy) => {
    if (!performedBy) {
      return { name: 'N/A', email: '', role: '' };
    }

    if (typeof performedBy === 'string') {
      return { name: performedBy, email: '', role: '' };
    }

    const name = performedBy.firstName && performedBy.lastName
      ? `${performedBy.firstName} ${performedBy.lastName}`
      : performedBy.firstName || performedBy.lastName || performedBy.name || performedBy.email || 'N/A';

    return {
      name,
      email: performedBy.email || '',
      role: performedBy.role || ''
    };
  };

  const parseCallMetadataDate = (value) => {
    if (!value) return null;

    if (typeof value === 'string') {
      const match = value
        .trim()
        .match(/^(\d{2})\/(\d{2})\/(\d{4}),?\s+(\d{2}):(\d{2})(?::(\d{2}))?$/);

      if (match) {
        const [, day, month, year, hour, minute, second = '0'] = match;
        return new Date(
          Number(year),
          Number(month) - 1,
          Number(day),
          Number(hour),
          Number(minute),
          Number(second)
        );
      }
    }

    return value;
  };

  const formatCallMetadataDate = (...values) => {
    const value = values.find((item) => item !== undefined && item !== null && String(item).trim() !== '');
    if (!value) return 'N/A';

    return formatSystemDate(parseCallMetadataDate(value), { includeTime: true }) || 'N/A';
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatTimeOnly = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'Invalid Time';
    }
  };

  // Helper function to map RP Digital call status to UI display text
  const getCallStatusDisplay = (status) => {
    if (!status) return null;
    const statusUpper = status.toUpperCase();
    const statusMap = {
      'ANSWER': 'Answered',
      'PICKED': 'Answered', // Picked means call was answered
      'NOANSWER': 'Missed / No Answer',
      'BUSY': 'Busy',
      'CANCEL-AGENT': 'Cancelled by Agent',
      'CANCEL-CUSTOMER': 'Cancelled by Customer',
      'FAILED': 'Failed',
      'PENDING': 'Pending'
    };
    return statusMap[statusUpper] || status;
  };

  // Helper function to get call status badge color
  const getCallStatusColor = (status) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    const statusUpper = status.toUpperCase();
    switch (statusUpper) {
      case 'ANSWER': return 'bg-green-100 text-green-800';
      case 'PICKED': return 'bg-green-100 text-green-800'; // Picked means answered
      case 'NOANSWER': return 'bg-yellow-100 text-yellow-800';
      case 'BUSY': return 'bg-orange-100 text-orange-800';
      case 'CANCEL-AGENT': return 'bg-red-100 text-red-800';
      case 'CANCEL-CUSTOMER': return 'bg-red-100 text-red-800';
      case 'FAILED': return 'bg-red-100 text-red-800';
      case 'PENDING': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Helper function to get the correct activity time
  // Priority: performedAt (when call was initiated) > callMetadata times > createdAt
  // This ensures consistent time display before and after webhook updates
  const getActivityTime = (activity) => {
    if (!activity) return new Date().toISOString();

    if (activity.activityNature === 'followup' && activity.scheduledFor) {
      return new Date(activity.scheduledFor).toISOString();
    }

    // Always prioritize performedAt first - this is when the call was actually initiated
    if (activity.performedAt) {
      return new Date(activity.performedAt).toISOString();
    }

    // For automated calls, fallback to callMetadata times if performedAt is not available
    if (activity.isAutomated && activity.callMetadata) {
      // Priority order: startTime > legBStartTime > legAPickedTime > legBPickedTime
      const callTime = activity.callMetadata.startTime ||
        activity.callMetadata.legBStartTime ||
        activity.callMetadata.legAPickedTime ||
        activity.callMetadata.legBPickedTime;

      if (callTime) {
        try {
          const date = new Date(callTime);
          if (!isNaN(date.getTime())) {
            return date.toISOString();
          }
        } catch (error) {
        }
      }
    }

    // Final fallback to createdAt
    return activity.createdAt || new Date().toISOString();
  };

  const formatDuration = (minutes, callDurationSeconds = null) => {
    // If callDurationSeconds is provided (from callMetadata), use exact seconds
    if (callDurationSeconds !== null && callDurationSeconds !== undefined) {
      const totalSeconds = Math.floor(callDurationSeconds);
      const hours = Math.floor(totalSeconds / 3600);
      const mins = Math.floor((totalSeconds % 3600) / 60);
      const secs = totalSeconds % 60;

      if (hours > 0) {
        if (mins === 0 && secs === 0) {
          return `${hours} hour${hours !== 1 ? 's' : ''}`;
        } else if (secs === 0) {
          return `${hours} hour${hours !== 1 ? 's' : ''} ${mins} minute${mins !== 1 ? 's' : ''}`;
        } else {
          return `${hours} hour${hours !== 1 ? 's' : ''} ${mins} minute${mins !== 1 ? 's' : ''} ${secs} second${secs !== 1 ? 's' : ''}`;
        }
      } else if (mins > 0) {
        if (secs === 0) {
          return `${mins} minute${mins !== 1 ? 's' : ''}`;
        } else {
          return `${mins} minute${mins !== 1 ? 's' : ''} ${secs} second${secs !== 1 ? 's' : ''}`;
        }
      } else {
        return `${secs} second${secs !== 1 ? 's' : ''}`;
      }
    }

    // Fallback to minutes (for non-automated calls or if callDuration not available)
    if (!minutes) return '';

    // Convert minutes to exact seconds for precise display
    const totalSeconds = Math.floor(minutes * 60);
    const hours = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;

    if (hours > 0) {
      if (mins === 0 && secs === 0) {
        return `${hours} hour${hours !== 1 ? 's' : ''}`;
      } else if (secs === 0) {
        return `${hours} hour${hours !== 1 ? 's' : ''} ${mins} minute${mins !== 1 ? 's' : ''}`;
      } else {
        return `${hours} hour${hours !== 1 ? 's' : ''} ${mins} minute${mins !== 1 ? 's' : ''} ${secs} second${secs !== 1 ? 's' : ''}`;
      }
    } else if (mins > 0) {
      if (secs === 0) {
        return `${mins} minute${mins !== 1 ? 's' : ''}`;
      } else {
        return `${mins} minute${mins !== 1 ? 's' : ''} ${secs} second${secs !== 1 ? 's' : ''}`;
      }
    } else {
      return `${secs} second${secs !== 1 ? 's' : ''}`;
    }
  };

  const formatSeconds = (seconds) => {
    if (seconds === null || seconds === undefined || Number.isNaN(Number(seconds))) {
      return 'N/A';
    }
    return formatDuration(null, Number(seconds));
  };

  const parseKeyValueNotes = (notes = '') => {
    return String(notes || '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .reduce((acc, line) => {
        const separatorIndex = line.indexOf(':');
        if (separatorIndex === -1) return acc;

        const key = line.slice(0, separatorIndex).trim().toLowerCase();
        const value = line.slice(separatorIndex + 1).trim();
        if (key && value) acc[key] = value;
        return acc;
      }, {});
  };

  const getEmailContent = (activity) => {
    const description = String(activity?.description || '');
    const subjectPrefix = 'Subject:';
    let subject = activity?.title || 'No subject';
    let message = description;

    if (description.trim().toLowerCase().startsWith(subjectPrefix.toLowerCase())) {
      const firstLineBreak = description.indexOf('\n');
      const subjectLine = firstLineBreak === -1 ? description : description.slice(0, firstLineBreak);
      subject = subjectLine.slice(subjectPrefix.length).trim() || subject;
      message = firstLineBreak === -1 ? '' : description.slice(firstLineBreak + 1).trim();
    }

    return {
      subject,
      message: message || 'No message provided',
      meta: parseKeyValueNotes(activity?.notes)
    };
  };

  const hasHtmlContent = (value = '') => /<\/?[a-z][\s\S]*>/i.test(String(value || ''));

  const handleDelete = async () => {
    // Validate activity first
    if (!activity || !activity._id) {
      toast.error('Activity information is missing');
      return;
    }

    // Show shorter confirmation dialog
    const confirmMessage = `Delete activity "${activity.title}"?\n\nThis action cannot be undone.`;

    // CRITICAL: Show confirmation dialog FIRST
    const confirmed = window.confirm(confirmMessage);

    // If user clicked Cancel, stop here - do NOT proceed
    if (confirmed !== true) {
      return; // User cancelled, exit immediately
    }

    setIsDeleting(true);

    try {
      const result = await customerActivityService.deleteActivity(activity._id);

      if (result.success) {
        toast.success('Activity deleted successfully');
        navigate('/customer_activities');
      } else {
        toast.error(result.error || 'Failed to delete activity');
      }
    } catch (error) {
      toast.error('An error occurred while deleting the activity');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEdit = () => {
    // Navigate to LogActivity page in edit mode with the activity's customer ID
    if (activity.customer && (activity.customer._id || activity.customer.id)) {
      const customerId = activity.customer._id || activity.customer.id;
      navigate(`/log_customer_activity/${customerId}?edit=true&activityId=${id}`);
    } else {
      toast.error('Cannot edit: Customer information not available');
    }
  };

  const handleExport = async () => {
    if (isExporting || !id) return;
    setIsExporting(true);
    const toastId = toast.loading('Preparing Excel export...');
    try {
      const exportName = activity?.title || activity?.type || activity?.activityId || 'Activity';
      const result = await customerActivityService.exportActivityDetail(id, exportName);
      if (result.success) {
        toast.success('Activity exported successfully', { id: toastId });
      } else {
        toast.error(result.error || 'Failed to export activity', { id: toastId });
      }
    } catch (err) {
      toast.error(err.message || 'Failed to export activity', { id: toastId });
    } finally {
      setIsExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agrosaf-green mx-auto mb-4"></div>
          <p className="text-gray-600">Loading activity details...</p>
        </div>
      </div>
    );
  }

  if (error || !activity) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="p-3 bg-red-100 rounded-full mb-4 mx-auto w-fit">
            <X className="h-8 w-8 text-red-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Activity</h3>
          <p className="text-gray-600 mb-4">{error || 'Activity not found'}</p>
          <Button
            variant="primary"
            icon={<ArrowLeft className="h-4 w-4" />}
            onClick={() => navigate(-1)}
          >
            Back to Activities
          </Button>
        </div>
      </div>
    );
  }

  const IconComponent = getActivityIcon(activity.type);

  return (
    <div className="p-6 mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
          <div className="flex items-center space-x-4 min-w-0 flex-1">
            <Button
              variant="outline"
              icon={<ArrowLeft className="h-4 w-4" />}
              onClick={() => navigate(-1)}
            >
              Back
            </Button>
            <div className="flex items-center space-x-3 min-w-0 flex-1">
              <div className="p-2 rounded-lg bg-gray-100 flex-shrink-0">
                <IconComponent className={`h-6 w-6 ${getActivityColor(activity.type)}`} />
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="text-2xl font-bold text-gray-900 break-words first-letter:capitalize">{activity.title}</h1>
                <p className="text-gray-600 capitalize">{activity.type} Activity</p>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-3 flex-shrink-0">
            <Button
              variant="outline"
              icon={<Download className="h-4 w-4" />}
              onClick={handleExport}
              loading={isExporting}
              disabled={isExporting}
              className="shadow-sm whitespace-nowrap"
            >
              {isExporting ? 'Exporting...' : 'Export'}
            </Button>
            {!isSalesExecutive && (
              <Button
                variant="outline"
                icon={<Edit className="h-4 w-4" />}
                onClick={handleEdit}
                className="hover:bg-green-50 hover:border-green-300 hover:text-green-700"
              >
                Edit
              </Button>
            )}
            {!isSalesExecutive && (
              <Button
                variant="outline"
                icon={<Trash2 className="h-4 w-4" />}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDelete();
                }}
                className="hover:bg-red-50 hover:border-red-300 hover:text-red-700"
                disabled={isDeleting}
                type="button"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Activity Details */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="space-y-6">
          {/* Basic Info */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Activity Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-start gap-4">
                    <span className="text-gray-600 flex-shrink-0 w-28">Activity ID:</span>
                    <span className="text-gray-900 font-mono font-medium flex-1 min-w-0">
                      {activity.activityId || activity._id || 'N/A'}
                    </span>
                  </div>
                </div>
                {activity.customer && (
                  <>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-start gap-4">
                        <span className="text-gray-600 flex-shrink-0 w-20">Customer:</span>
                        <span className="text-gray-900 font-medium break-words flex-1 min-w-0 capitalize">
                          {getCustomerName(activity.customer)}
                        </span>
                      </div>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-start gap-4">
                        <span className="text-gray-600 flex-shrink-0 w-20">Company:</span>
                        <span className="text-gray-900 break-words flex-1 min-w-0 first-letter:capitalize">
                          {activity.customer.companyName || 'No company'}
                        </span>
                      </div>
                    </div>
                    {activity.customer.email && (
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-start gap-4">
                          <span className="text-gray-600 flex-shrink-0 w-20">Email:</span>
                          <span className="text-gray-900 break-all flex-1 min-w-0 lowercase">
                            {activity.customer.email}
                          </span>
                        </div>
                      </div>
                    )}
                  </>
                )}
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-4">
                    <span className="text-gray-600 flex-shrink-0 w-28">Status:</span>
                    {(activity.activityStatus?.name || activity.outcome) ? (
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full flex-shrink-0 capitalize ${getStatusColor(activity.activityStatus?.name?.toLowerCase() || activity.outcome)}`}>
                        {activity.activityStatus?.name || activity.outcome}
                      </span>
                    ) : (
                      <span className="text-gray-500 text-xs italic font-normal">N/A</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                {(() => {
                  const isFollowUp = activity.activityNature === 'followup';
                  return (
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-4">
                        <span className="text-gray-600 flex-shrink-0 w-32 whitespace-nowrap">Activity Nature:</span>
                        <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold ${isFollowUp ? 'bg-amber-100 text-amber-800 border border-amber-200' : 'bg-gray-100 text-gray-700 border border-gray-200'}`}>
                          {isFollowUp ? 'Follow Up' : 'Past Activity'}
                        </span>
                      </div>
                    </div>
                  );
                })()}
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-start gap-4">
                    <span className="text-gray-600 flex-shrink-0 w-28">Performed By:</span>
                    <div className="text-gray-900 flex-1 min-w-0">
                      {(() => {
                        const assignee = getAssigneeDetails(activity.performedBy);
                        return (
                          <div className="space-y-0.5 break-words">
                            <div className="font-medium capitalize">{assignee.name}</div>
                            {assignee.email && (
                              <div className="text-xs text-gray-500 break-all lowercase">{assignee.email}</div>
                            )}
                            {assignee.role && (
                              <div className="text-xs text-gray-500 capitalize">{assignee.role.replace(/_/g, ' ')}</div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-4">
                    <span className="text-gray-600 flex-shrink-0 w-28">Created At:</span>
                    <span className="text-gray-900 flex-shrink-0">
                      {activity.createdAt ? `${formatDateOnly(activity.createdAt)} ${formatTimeOnly(activity.createdAt)}` : 'N/A'}
                    </span>
                  </div>
                </div>
                {(() => {
                  // For meetings, show duration if available
                  if (activity.type === 'meeting' && activity.duration) {
                    return (
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-4">
                          <span className="text-gray-600 flex-shrink-0 w-28">Duration:</span>
                          <span className="text-gray-900 flex-shrink-0">{formatDuration(activity.duration)}</span>
                        </div>
                      </div>
                    );
                  }

                  // For calls, ONLY show actual talk time (talkDuration) - no fallbacks
                  // This means only answered/picked calls that had actual conversation will show duration
                  const talkDuration = activity.callMetadata?.talkDuration;

                  // Only show if we have actual talk time - no fallbacks
                  return talkDuration !== null && talkDuration !== undefined && talkDuration > 0 ? (
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-4">
                        <span className="text-gray-600 flex-shrink-0 w-28">Duration:</span>
                        <span className="text-gray-900 flex-shrink-0">{formatDuration(null, talkDuration)}</span>
                      </div>
                    </div>
                  ) : null;
                })()}
                {activity.type === 'call' && activity.callMetadata?.status && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-4">
                      <span className="text-gray-600 flex-shrink-0 w-28">Call Status:</span>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getCallStatusColor(activity.callMetadata.status)}`}>
                        {getCallStatusDisplay(activity.callMetadata.status)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Description */}
          {activity.type === 'email' ? (
            (() => {
              const emailContent = getEmailContent(activity);
              return (
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-3">Email Message</h4>
                  <div className="overflow-hidden rounded-xl border border-blue-100 bg-white shadow-sm">
                    <div className="bg-blue-50 px-5 py-4 border-b border-blue-100">
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 h-10 w-10 rounded-full bg-blue-600 text-white flex items-center justify-center flex-shrink-0">
                          <Mail className="h-5 w-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-semibold uppercase tracking-wide text-blue-700 mb-1">Subject</p>
                          <h3 className="text-lg font-bold text-gray-900 break-words uppercase">{emailContent.subject}</h3>
                        </div>
                      </div>
                    </div>

                    <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/70">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="flex items-start gap-3">
                          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 w-20 flex-shrink-0">To</span>
                          <span className="text-sm font-medium text-gray-900 break-all">{emailContent.meta.recipient || activity.customer?.email || 'N/A'}</span>
                        </div>
                        {emailContent.meta.cc && (
                          <div className="flex items-start gap-3">
                            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 w-20 flex-shrink-0">CC</span>
                            <span className="text-sm font-medium text-gray-900 break-all">{emailContent.meta.cc}</span>
                          </div>
                        )}
                        <div className="flex items-start gap-3">
                          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 w-20 flex-shrink-0">Sent At</span>
                          <span className="text-sm font-medium text-gray-900">
                            {formatDateOnly(getActivityTime(activity))} {formatTimeOnly(getActivityTime(activity))}
                          </span>
                        </div>
                        {emailContent.meta.attachments && (
                          <div className="flex items-start gap-3">
                            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 w-20 flex-shrink-0">Files</span>
                            <span className="text-sm font-medium text-gray-900 break-words">{emailContent.meta.attachments}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="p-5">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">Message</p>
                      {hasHtmlContent(emailContent.message) ? (
                        <div
                          className="rounded-lg border border-gray-100 bg-white p-4 text-gray-800 break-words leading-relaxed min-h-[120px] prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(emailContent.message) }}
                        />
                      ) : (
                        <div className="rounded-lg border border-gray-100 bg-white p-4 text-gray-800 whitespace-pre-wrap break-words leading-relaxed min-h-[120px]">
                          {emailContent.message}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()
          ) : (
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-2">Description</h4>
              <p className="text-gray-700 bg-gray-50 p-3 rounded-lg break-words first-letter:capitalize">{activity.description || 'No description provided'}</p>
            </div>
          )}

          {/* Call Details - only for call activities */}
          {activity.type === 'call' && activity.callMetadata && (
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-2">Call Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="flex items-start gap-4">
                      <span className="text-gray-600 flex-shrink-0 w-36">Source Number:</span>
                      <span className="text-gray-900 break-all flex-1 min-w-0">{activity.callMetadata.sourceNumber || 'N/A'}</span>
                    </div>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="flex items-start gap-4">
                      <span className="text-gray-600 flex-shrink-0 w-36">Destination Number:</span>
                      <span className="text-gray-900 break-all flex-1 min-w-0">{(activity.callMetadata.destinationNumber || '').trim() || 'N/A'}</span>
                    </div>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="flex items-start gap-4">
                      <span className="text-gray-600 flex-shrink-0 w-36">Dialed Number:</span>
                      <span className="text-gray-900 break-all flex-1 min-w-0">{activity.callMetadata.dialWhomNumber || 'N/A'}</span>
                    </div>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="flex items-center gap-4">
                      <span className="text-gray-600 flex-shrink-0 w-36">Direction:</span>
                      <span className="text-gray-900 flex-shrink-0 uppercase">{activity.callMetadata.direction || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="flex items-center gap-4">
                      <span className="text-gray-600 flex-shrink-0 w-36">Call Duration:</span>
                      <span className="text-gray-900 flex-shrink-0">{formatSeconds(activity.callMetadata.callDuration)}</span>
                    </div>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="flex items-center gap-4">
                      <span className="text-gray-600 flex-shrink-0 w-36">Talk Duration:</span>
                      <span className="text-gray-900 flex-shrink-0">{formatSeconds(activity.callMetadata.talkDuration)}</span>
                    </div>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="flex items-center gap-4">
                      <span className="text-gray-600 flex-shrink-0 w-36">Coins:</span>
                      <span className="text-gray-900 flex-shrink-0">{activity.callMetadata.coins ?? 'N/A'}</span>
                    </div>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="flex items-center gap-4">
                      <span className="text-gray-600 flex-shrink-0 w-36">Call SID:</span>
                      <span className="text-gray-900 break-all flex-1 min-w-0">{activity.callMetadata.callSid || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className="space-y-3">
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <div className="flex items-center gap-4">
                      <span className="text-gray-600 flex-shrink-0 w-36">Start Time:</span>
                      <span className="text-gray-900 flex-shrink-0">
                        {formatCallMetadataDate(
                          activity.callMetadata.startTime,
                          activity.callMetadata.startdatetime,
                          activity.callMetadata.legBStartTime,
                          activity.callMetadata.legAStartTime
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <div className="flex items-center gap-4">
                      <span className="text-gray-600 flex-shrink-0 w-36">End Time:</span>
                      <span className="text-gray-900 flex-shrink-0">
                        {formatCallMetadataDate(
                          activity.callMetadata.endTime,
                          activity.callMetadata.enddatetime,
                          activity.callMetadata.hangupTime
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <div className="flex items-center gap-4">
                      <span className="text-gray-600 flex-shrink-0 w-36">Campaign ID:</span>
                      <span className="text-gray-900 break-all flex-1 min-w-0">{activity.callMetadata.campaignId || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {(activity.callMetadata.rpDigitalCallId || activity.callMetadata.callRecordingUrl) && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
                  {activity.callMetadata.rpDigitalCallId && (
                    <div className="flex items-center gap-4 mb-3">
                      <span className="text-gray-600 flex-shrink-0 w-36">RP Digital ID:</span>
                      <span className="text-gray-900 break-all flex-1 min-w-0">{activity.callMetadata.rpDigitalCallId}</span>
                    </div>
                  )}

                  {activity.callMetadata.callRecordingUrl && (
                    <div className="flex items-start gap-4">
                      <span className="text-gray-600 flex-shrink-0 w-36">Call Recording:</span>
                      <a
                        href={activity.callMetadata.callRecordingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline break-all"
                      >
                        Open Recording
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Activity Date & Time */}
          {activity.scheduledFor && (
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-2">Activity Date & Time</h4>
              <div className="flex items-center space-x-2 text-blue-600 bg-blue-50 p-3 rounded-lg">
                <Calendar className="h-5 w-5 flex-shrink-0" />
                <div>
                  <div>{formatDateOnly(activity.scheduledFor)}</div>
                  <div className="text-sm">{formatTimeOnly(activity.scheduledFor)}</div>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div >
  );
};

export default CustomerActivityDetail;
